/**
 * TypeScript models mirroring the EPS Public referral create contract.
 *
 * Source of truth (EPS repo LD.Shared.EnterprisePlatformServices.API):
 *   EPS/Public/LD.EPS.Public.Api/Models/ThirdParty/Referral/*.cs
 *
 * The EPS Web API serializes these as camelCase JSON, so the property names
 * here match the JSON contract, not the C# PascalCase field names.
 */

export const LOAN_PURPOSES = ["purchase", "refinance"] as const;
export type LoanPurpose = (typeof LOAN_PURPOSES)[number];

export const PROPERTY_TYPES = [
  "SingleFamily",
  "Condo",
  "Townhouse",
  "MultiFamily",
  "ManufacturedHome",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const REFINANCE_TYPES = ["RateTermRefinance", "CashOutRefinance"] as const;
export type RefinanceType = (typeof REFINANCE_TYPES)[number];

export const CREDIT_SCORE_RANGES = ["below 620", "620-679", "680-739", "740+"] as const;
export type CreditScoreRange = (typeof CREDIT_SCORE_RANGES)[number];

export const STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP",
] as const;
export type StateCode = (typeof STATE_CODES)[number];

export const EMPLOYMENT_STATUSES = [
  "Employed",
  "SelfEmployed",
  "Retired",
  "Military",
  "NotEmployed",
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

export const INCOME_RANGES = ["under50k", "50k-75k", "75k-100k", "100k-150k", "over150k"] as const;
export type IncomeRange = (typeof INCOME_RANGES)[number];

export const MILITARY_BRANCHES = [
  "Army",
  "Navy",
  "Air Force",
  "Marine Corps",
  "Coast Guard",
  "Space Force",
] as const;
export type MilitaryBranch = (typeof MILITARY_BRANCHES)[number];

export const MILITARY_SERVICE_STATUSES = [
  "Active",
  "Veteran",
  "Retired",
  "National Guard",
  "Surviving spouse",
] as const;
export type MilitaryServiceStatus = (typeof MILITARY_SERVICE_STATUSES)[number];

/**
 * A constrained select option: `value` is the exact string submitted to / read
 * from the EPS Public referral contract, `label` is the user-friendly English
 * text shown in the UI. Prefill and payload building always operate on `value`,
 * never `label`, so the API contract is preserved regardless of display text.
 */
export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

function toOptions<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): readonly SelectOption[] {
  return values.map((value) => ({ value, label: labels[value] }));
}

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  SingleFamily: "Single-family residence",
  Condo: "Condominium",
  Townhouse: "Townhouse",
  MultiFamily: "Multi-family home",
  ManufacturedHome: "Manufactured home",
};

export const REFINANCE_TYPE_LABELS: Record<RefinanceType, string> = {
  RateTermRefinance: "Rate-and-term refinance",
  CashOutRefinance: "Cash-out refinance",
};

export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  Employed: "Employed",
  SelfEmployed: "Self-employed",
  Retired: "Retired",
  Military: "Active military",
  NotEmployed: "Not employed",
};

export const INCOME_RANGE_LABELS: Record<IncomeRange, string> = {
  under50k: "Under $50,000",
  "50k-75k": "$50,000 – $75,000",
  "75k-100k": "$75,000 – $100,000",
  "100k-150k": "$100,000 – $150,000",
  over150k: "Over $150,000",
};

export const CREDIT_SCORE_RANGE_LABELS: Record<CreditScoreRange, string> = {
  "below 620": "Below 620",
  "620-679": "620 – 679",
  "680-739": "680 – 739",
  "740+": "740 or higher",
};

/** Military branch values are already user-friendly, so label === value. */
export const MILITARY_BRANCH_LABELS: Record<MilitaryBranch, string> = {
  Army: "Army",
  Navy: "Navy",
  "Air Force": "Air Force",
  "Marine Corps": "Marine Corps",
  "Coast Guard": "Coast Guard",
  "Space Force": "Space Force",
};

/**
 * Military service status values equal their labels by existing design (they are
 * free-text on the EPS contract, not a validated enum). This is intentionally
 * kept as value === label so no new persistence mapping is introduced.
 */
export const MILITARY_SERVICE_STATUS_LABELS: Record<MilitaryServiceStatus, string> = {
  Active: "Active",
  Veteran: "Veteran",
  Retired: "Retired",
  "National Guard": "National Guard",
  "Surviving spouse": "Surviving spouse",
};

export const STATE_LABELS: Record<StateCode, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia", PR: "Puerto Rico", VI: "U.S. Virgin Islands", GU: "Guam",
  AS: "American Samoa", MP: "Northern Mariana Islands",
};

export const PROPERTY_TYPE_OPTIONS = toOptions(PROPERTY_TYPES, PROPERTY_TYPE_LABELS);
export const REFINANCE_TYPE_OPTIONS = toOptions(REFINANCE_TYPES, REFINANCE_TYPE_LABELS);
export const EMPLOYMENT_STATUS_OPTIONS = toOptions(EMPLOYMENT_STATUSES, EMPLOYMENT_STATUS_LABELS);
export const INCOME_RANGE_OPTIONS = toOptions(INCOME_RANGES, INCOME_RANGE_LABELS);
export const CREDIT_SCORE_RANGE_OPTIONS = toOptions(CREDIT_SCORE_RANGES, CREDIT_SCORE_RANGE_LABELS);
export const MILITARY_BRANCH_OPTIONS = toOptions(MILITARY_BRANCHES, MILITARY_BRANCH_LABELS);
export const MILITARY_SERVICE_STATUS_OPTIONS = toOptions(
  MILITARY_SERVICE_STATUSES,
  MILITARY_SERVICE_STATUS_LABELS,
);
export const STATE_OPTIONS = toOptions(STATE_CODES, STATE_LABELS);

/** Human-friendly label for a property type value, falling back to the raw value. */
export function propertyTypeLabel(value: string): string {
  return (PROPERTY_TYPE_LABELS as Record<string, string>)[value] ?? value;
}

/** Human-friendly label for a refinance type value, falling back to the raw value. */
export function refinanceTypeLabel(value: string): string {
  return (REFINANCE_TYPE_LABELS as Record<string, string>)[value] ?? value;
}

/** ReferralBorrowerRequest */
export interface ReferralBorrowerRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  creditScoreRange?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

/** MilitaryServiceRequest */
export interface MilitaryServiceRequest {
  isMilitary: boolean;
  branch?: string;
  serviceStatus?: string;
}

/** EmploymentInfoRequest */
export interface EmploymentInfoRequest {
  employmentStatus?: string;
  incomeRange?: string;
}

/** CreatePurchaseReferralRequest */
export interface CreatePurchaseReferralRequest {
  partnerReferenceId?: string;
  borrower: ReferralBorrowerRequest;
  coBorrower?: ReferralBorrowerRequest;
  militaryService?: MilitaryServiceRequest;
  employment?: EmploymentInfoRequest;
  propertyState: string;
  propertyCity?: string;
  propertyZipCode?: string;
  propertyStreetAddress?: string;
  propertyType: string;
  estimatedValue: number;
  loanAmount: number;
}

/** CreateRefinanceReferralRequest */
export interface CreateRefinanceReferralRequest {
  partnerReferenceId?: string;
  borrower: ReferralBorrowerRequest;
  coBorrower?: ReferralBorrowerRequest;
  militaryService?: MilitaryServiceRequest;
  employment?: EmploymentInfoRequest;
  propertyState: string;
  propertyCity?: string;
  propertyZipCode?: string;
  propertyStreetAddress?: string;
  propertyType: string;
  currentLoanBalance: number;
  estimatedValue: number;
  loanAmount: number;
  refinanceType: string;
}

export type CreateReferralRequest =
  | CreatePurchaseReferralRequest
  | CreateRefinanceReferralRequest;

/** ReferralLinks */
export interface ReferralLinks {
  status?: string;
  detail?: string;
}

/** CreateReferralResponse */
export interface CreateReferralResponse {
  referralId: string;
  partnerReferenceId?: string;
  status: string;
  submittedAt: string;
  links?: ReferralLinks;
}

/**
 * Flat borrower form state. All values are raw strings straight from the DOM
 * inputs; the payload builder is responsible for trimming and shaping them.
 */
export interface BorrowerFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  creditScoreRange: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

/** Complete referral form state as read from the DOM. */
export interface ReferralFormState {
  loanPurpose: LoanPurpose | "";
  partnerReferenceId: string;
  borrower: BorrowerFormState;
  includeCoBorrower: boolean;
  coBorrower: BorrowerFormState;
  isMilitary: boolean;
  militaryBranch: string;
  militaryServiceStatus: string;
  employmentStatus: string;
  incomeRange: string;
  propertyStreetAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  propertyType: string;
  /**
   * Purchase-only: the borrower has not found a property yet. When true, the
   * street address, city, and ZIP fields are hidden and omitted from the payload
   * while the required property/loan detail fields remain.
   */
  propertyStillLooking: boolean;
  estimatedValue: string;
  loanAmount: string;
  currentLoanBalance: string;
  refinanceType: string;
}

export function emptyBorrowerState(): BorrowerFormState {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    creditScoreRange: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
  };
}

export function emptyFormState(): ReferralFormState {
  return {
    loanPurpose: "purchase",
    partnerReferenceId: "",
    borrower: emptyBorrowerState(),
    includeCoBorrower: false,
    coBorrower: emptyBorrowerState(),
    isMilitary: true,
    militaryBranch: "",
    militaryServiceStatus: "",
    employmentStatus: "",
    incomeRange: "",
    propertyStreetAddress: "",
    propertyCity: "",
    propertyState: "",
    propertyZipCode: "",
    propertyType: "",
    propertyStillLooking: false,
    estimatedValue: "",
    loanAmount: "",
    currentLoanBalance: "",
    refinanceType: "",
  };
}
